/**
 * Sold-out products are hidden while the collection is paging, then revealed
 * in one block at the very end - after the last page of in-stock products has
 * loaded. They are never removed, so shoppers can still find them and use
 * "Notify me when available".
 *
 * ---------------------------------------------------------------------------
 * WHY HIDING, RATHER THAN JUST SORTING
 * ---------------------------------------------------------------------------
 * Earlier versions moved sold-out cards to the end on every append. Correct as
 * an ordering, wrong as an experience: the sold-out block sits at the bottom,
 * which is exactly where you are when the next page triggers. The new page
 * lands after it, the block is moved past the new cards, and you scroll past
 * the same sold-out products on every page load - with a shift each time.
 *
 * Hiding removes the cause. A hidden element has no layout, so moving it costs
 * nothing visually and needs no scroll correction. They are revealed once, at
 * the end, where appending below the reader cannot disturb anything.
 *
 * ---------------------------------------------------------------------------
 * THE INDICATOR IS A SPINNER ONLY
 * ---------------------------------------------------------------------------
 * It used to read "Still loading out of stock products...". Two problems:
 * paging is fast enough now that the sentence was on screen for a moment and
 * gone, and it names sold-out products while sitting under a grid that is
 * mostly in stock, which reads as if the products above it were the sold-out
 * ones. A bare spinner says "not finished" without making a claim about what.
 *
 * The wording is kept for screen readers in a visually hidden span, because a
 * spinner alone announces nothing. role="status" with aria-live="polite" means
 * it is read when it appears rather than interrupting.
 *
 * ---------------------------------------------------------------------------
 * THE BACK BUTTON BUG - why 'revealed' is reset on pageshow
 * ---------------------------------------------------------------------------
 * Sold-out products appeared loose in the grid with no indicator, on a page
 * that behaves correctly on a fresh load. Two reasonable things combined:
 * pagehide calls revealAll() so nobody leaves with products hidden, and
 * revealed is a module flag that disables all further deferring.
 *
 * The back/forward cache does not re-run the script - it restores the whole JS
 * heap, revealed === true included. So visiting a product and pressing Back
 * returned you to a page where deferring was permanently off. pageshow with
 * event.persisted is the only signal that a restore happened.
 *
 * ---------------------------------------------------------------------------
 * ORDER IS MAINTAINED EVEN AFTER REVEALING
 * ---------------------------------------------------------------------------
 * Revealing used to end this file's involvement, assuming nothing is appended
 * afterwards. If that is ever wrong - a premature 'exhausted' from a failed
 * fetch, an app injecting cards - sold-out products end up interleaved and
 * nothing corrects it. So once revealed they are still kept at the end, just
 * without hiding, with the same anchor-based scroll compensation.
 *
 * INLINE display IS DELIBERATE: hiding sets el.style.display = 'none' and
 * revealing sets it back to '', restoring whatever the stylesheet says per
 * breakpoint. Hard-coding 'block' would break the grid at some widths.
 */
(function () {
  'use strict';

  if (window.__soldOutSortInit) return;
  window.__soldOutSortInit = true;

  var DEFERRED = 'data-soldout-deferred';
  var NOTICE = 'soldout-pending';
  var LABEL = 'Loading more products';
  var revealed = false;

  function injectStyles() {
    if (document.getElementById('soldout-pending-css')) return;
    var css =
      '.' + NOTICE + '{grid-column:1/-1;display:flex;align-items:center;justify-content:center;' +
        'padding:32px 0 12px;}' +
      '.' + NOTICE + '__spinner{width:22px;height:22px;flex:0 0 auto;border-radius:50%;' +
        'border:2px solid currentColor;border-top-color:transparent;opacity:.55;' +
        'animation:soldout-pending-spin .8s linear infinite;}' +
      '@keyframes soldout-pending-spin{to{transform:rotate(360deg)}}' +
      '@media (prefers-reduced-motion:reduce){.' + NOTICE + '__spinner{animation:none;opacity:.35}}' +
      /* Announced, not shown - see THE INDICATOR IS A SPINNER ONLY. */
      '.' + NOTICE + '__sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
        'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}';
    var style = document.createElement('style');
    style.id = 'soldout-pending-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function isSoldOut(el) {
    return el.getAttribute('data-available') === 'false';
  }

  function cards(grid) {
    return Array.prototype.slice.call(grid.children).filter(function (el) {
      return el.tagName && el.tagName.toLowerCase() === 'product-block';
    });
  }

  function anchorOf(items, moving) {
    for (var i = 0; i < items.length; i++) {
      if (moving.indexOf(items[i]) !== -1) continue;
      if (items[i].getBoundingClientRect().bottom > 0) return items[i];
    }
    return null;
  }

  /* Move sold-out cards to the end. hide=true also takes them out of layout. */
  function orderGrid(grid, hide) {
    var items = cards(grid);
    if (!items.length) return;

    var sold = items.filter(isSoldOut);
    if (!sold.length) return;

    var pendingHide = hide
      ? sold.filter(function (el) { return !el.hasAttribute(DEFERRED); })
      : [];

    var tail = items.slice(items.length - sold.length);
    if (pendingHide.length === 0 && tail.every(isSoldOut)) return;

    var willShift = hide
      ? pendingHide.some(function (el) { return el.style.display !== 'none'; })
      : true;
    var compensate = window.pageYOffset > 100 && willShift;
    var anchor = compensate ? anchorOf(items, sold) : null;
    var before = anchor ? anchor.getBoundingClientRect().top : 0;

    sold.forEach(function (el) {
      if (hide) {
        el.setAttribute(DEFERRED, '1');
        el.style.display = 'none';
      }
      grid.appendChild(el);
    });

    if (anchor) {
      var delta = anchor.getBoundingClientRect().top - before;
      if (Math.abs(delta) >= 1) window.scrollBy(0, delta);
    }
  }

  function buildNotice() {
    injectStyles();
    var note = document.createElement('div');
    note.className = NOTICE;
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');

    var spinner = document.createElement('span');
    spinner.className = NOTICE + '__spinner';
    spinner.setAttribute('aria-hidden', 'true');

    var sr = document.createElement('span');
    sr.className = NOTICE + '__sr';
    sr.textContent = LABEL;

    note.appendChild(spinner);
    note.appendChild(sr);
    return note;
  }

  function updateNotice() {
    document.querySelectorAll('.product-grid').forEach(function (grid) {
      var note = grid.querySelector('.' + NOTICE);
      var waiting = !revealed && grid.querySelector('[' + DEFERRED + ']');

      if (!waiting) {
        if (note) note.remove();
        return;
      }

      if (!note) note = buildNotice();

      /* Keep it last - later pages of in-stock products are appended after it.
         The guard makes this a no-op once it is already last, so it cannot
         re-trigger the observer that called it. */
      if (grid.lastElementChild !== note) grid.appendChild(note);
    });
  }

  function apply() {
    document.querySelectorAll('.product-grid').forEach(function (grid) {
      orderGrid(grid, !revealed);
    });
    updateNotice();
  }

  function revealAll() {
    if (revealed) return;
    revealed = true;

    document.querySelectorAll('[' + DEFERRED + ']').forEach(function (el) {
      el.style.display = '';
      el.removeAttribute(DEFERRED);
    });

    document.querySelectorAll('.' + NOTICE).forEach(function (el) { el.remove(); });
  }

  /* New query, or a restored page - hide again and wait for a fresh signal. */
  function reset() {
    revealed = false;
    apply();
  }

  var observer = null;
  var timer = null;

  /* setTimeout, not requestAnimationFrame: rAF never fires while the tab is
     hidden, and an earlier version latched a flag that was then never cleared,
     permanently killing this in background tabs. */
  function onMutation() {
    if (timer !== null) return;
    timer = window.setTimeout(function () {
      timer = null;
      apply();
    }, 50);
  }

  function startObserving() {
    /* Held for the page lifetime - an unreferenced MutationObserver is
       eligible for garbage collection in Chrome. */
    if (observer || !document.body) return;
    observer = new MutationObserver(onMutation);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    apply();
    startObserving();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('infinite-scroll:products-appended', apply);
  document.addEventListener('infinite-scroll:exhausted', revealAll);
  document.addEventListener('infinite-scroll:reset', reset);
  document.addEventListener('shopify:section:load', apply);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) apply();
  });

  /* Never leave the page with products hidden... */
  window.addEventListener('pagehide', revealAll);

  /* ...but a bfcache restore brings revealed === true back with it. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reset();
  });
})();
